import { useEffect, useState, useRef, useCallback } from 'react';
import { Plus, Filter } from 'lucide-react';
import type { ContentItem, ContentType, ContentChannel } from '../../types/content';
import { useContentStore } from '../../stores/contentStore';
import Spinner from '../common/Spinner';
import ContentKanban from './ContentKanban';
import CreateContentModal from './CreateContentModal';
import ContentDetailModal from './ContentDetailModal';

const CONTENT_TYPES: ContentType[] = ['social', 'email', 'blog', 'ad', 'video'];
const CHANNELS: ContentChannel[] = ['instagram', 'tiktok', 'pinterest', 'email', 'blog', 'facebook', 'etsy'];
const STATUSES = ['idea', 'draft', 'review', 'scheduled', 'published'];

export default function ContentPage() {
  const loading = useContentStore((s) => s.loading);
  const items = useContentStore((s) => s.items);
  const stats = useContentStore((s) => s.stats);
  const filters = useContentStore((s) => s.filters);
  const fetchPipeline = useContentStore((s) => s.fetchPipeline);
  const fetchStats = useContentStore((s) => s.fetchStats);
  const setFilters = useContentStore((s) => s.setFilters);

  const [showCreate, setShowCreate] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(() => {
    fetchPipeline();
    fetchStats();
  }, [fetchPipeline, fetchStats]);

  useEffect(() => {
    poll();
    intervalRef.current = setInterval(poll, 60000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [poll]);

  // Keep selected item fresh from the items array
  useEffect(() => {
    if (selectedItem) {
      const fresh = items.find((i) => i.id === selectedItem.id);
      if (!fresh) {
        setSelectedItem(null);
      }
    }
  }, [items, selectedItem]);

  const total = stats?.total ?? 0;
  const byStatus = stats?.byStatus ?? {};

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Content Pipeline</h1>
        <div className="flex items-center gap-3">
          {loading ? <Spinner size="sm" /> : null}
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 bg-accent-purple text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Content
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="bg-bg-secondary border border-border-subtle rounded-lg px-3 py-2">
          <span className="text-xs text-text-muted">Total</span>
          <span className="ml-2 text-sm font-semibold text-text-primary">{total}</span>
        </div>
        {STATUSES.map((s) => (
          <div key={s} className="bg-bg-secondary border border-border-subtle rounded-lg px-3 py-2">
            <span className="text-xs text-text-muted capitalize">{s}</span>
            <span className="ml-2 text-sm font-semibold text-text-primary">{byStatus[s] || 0}</span>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="h-4 w-4 text-text-muted" />
        <select
          value={filters.content_type || ''}
          onChange={(e) => setFilters({ content_type: e.target.value || undefined })}
          className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-2 py-1 text-sm"
        >
          <option value="">All Types</option>
          {CONTENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={filters.channel || ''}
          onChange={(e) => setFilters({ channel: e.target.value || undefined })}
          className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-2 py-1 text-sm"
        >
          <option value="">All Channels</option>
          {CHANNELS.map((c) => (
            <option key={c} value={c}>
              {c.charAt(0).toUpperCase() + c.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={filters.status || ''}
          onChange={(e) => setFilters({ status: e.target.value || undefined })}
          className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-2 py-1 text-sm"
        >
          <option value="">All Statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
        {(filters.content_type || filters.channel || filters.status) ? (
          <button
            onClick={() => setFilters({ content_type: undefined, channel: undefined, status: undefined })}
            className="text-xs text-accent-purple-light hover:text-accent-purple transition-colors"
          >
            Clear filters
          </button>
        ) : null}
      </div>

      {/* Kanban board */}
      <ContentKanban onItemClick={(item) => setSelectedItem(item)} />

      {showCreate ? (
        <CreateContentModal onClose={() => setShowCreate(false)} />
      ) : null}

      {selectedItem ? (
        <ContentDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      ) : null}
    </div>
  );
}
